import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Shipping } from "@faire/web--source/slate/icons/foundation/Shipping";
import React from "react";

import { SelfServeNodes } from "../../../types";

import { useOrderCanceledBrandNodes } from "./useOrderCanceledBrandNodes";
import { useOrderDeliveredBrandNodes } from "./useOrderDeliveredBrandNodes";
import { useOrderNewBrandNodes } from "./useOrderNewBrandNodes";
import { useOrderShippedBrandNodes } from "./useOrderShippedBrandNodes";
import { useOrderUnfulfilledBrandNodes } from "./useOrderUnfulfilledBrandNodes";
import { useOrderUnknownBrandNodes } from "./useOrderUnknownBrandNodes";

export const useOrdersBrandNodes = (): SelfServeNodes => {
  const { strictLocalize } = useStrictLocalization();
  const orderUnknown = useOrderUnknownBrandNodes();
  const orderNew = useOrderNewBrandNodes();
  const orderUnfulfilled = useOrderUnfulfilledBrandNodes();
  const orderShipped = useOrderShippedBrandNodes();
  const orderDelivered = useOrderDeliveredBrandNodes();
  const orderCanceled = useOrderCanceledBrandNodes();

  return {
    Orders: {
      id: "Orders",
      label: strictLocalize({
        defaultMessage: "Orders",
        description: {
          text: "A top level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      icon: <Shipping fontSize="24px" color="faireBlack" />,
      children: [
        // unknown state nodes
        "AddOrEditItemsUnknownState",
        "EditOrderInformationUnknownState",
        "InternationalOrderSettingsUnknownState",
        "DamageMissingItemReportUnknownState",
        "RequestCommissionChangesUnknownState",
        "CancelOrderUnknownState",
        "CannotFindAnOrderUnknownState",
        // new state nodes
        "QuestionAboutCustomerNewState",
        "AddOrEditItemsNewState",
        "EditOrderInformationNewState",
        "InternationalOrderSettingsNewState",
        "RequestCommissionChangesNewState",
        "InsiderFreeShippingNewState",
        "SplitOrderNewState",
        "CancelOrderNewState",
        // unfulfilled state nodes
        "AddOrEditItemsUnfulfilledState",
        "EditOrderInformationUnfulfilledState",
        "VatIssueUnfulfilledState",
        "SplitOrderUnfulfilledState",
        "CombineOrdersUnfulfilledState",
        "RequestCommissionChangesUnfulfilledState",
        "CancelOrderUnfulfilledState",
        // shipped state nodes
        "UpdateTrackingInformationShippedState",
        "ProblemWithDeliveryShippedState",
        "PayoutCalculationShippedState",
        "InvoiceDetailsShippedState",
        "RequestCommissionChangesShippedState",
        // delivered state nodes
        "DamageMissingItemReportDeliveredState",
        "PayoutCalculationDeliveredState",
        "InvoiceDetailsDeliveredState",
        "ProblemWithDeliveryDeliveredState",
        "RequestCommissionChangesDeliveredState",
        // canceled state nodes
        "WhyWasThisCanceledCanceledState",
        "OtherCanceledState",
      ],
      level: 1,
      sidebarTitle: strictLocalize({
        defaultMessage: "Order issue",
        description: {
          text: "Title for displaying the issue on the order summary sidebar",
        },
      }),
    },
    ...orderUnknown,
    ...orderNew,
    ...orderUnfulfilled,
    ...orderShipped,
    ...orderDelivered,
    ...orderCanceled,
  };
};
